import { useEffect, useMemo, useRef } from 'react';
import { GridApi, GridOptions } from 'ag-grid-community';
import { PinnedColumns, Row, UseControlledAGGridProps } from '../types';
import { getOnColumnPinned } from '../utils';

export const usePinnable = <
  RowData,
  FullWidthRowData = never,
  RowId extends string = string,
>(
  props: UseControlledAGGridProps<RowData, FullWidthRowData, RowId>,
  api: GridApi | null
) => {
  const pinnable = props.pinnable;
  const pinnableEnabled = pinnable !== undefined;
  const pinnableRef = useRef(pinnable);
  useEffect(() => {
    pinnableRef.current = pinnable;
  }, [pinnable]);
  const pinnedColumns: PinnedColumns<RowData> = useMemo(
    () =>
      pinnableEnabled && pinnable.pinnedColumns ? pinnable.pinnedColumns : {},
    [pinnable, pinnableEnabled]
  );

  useEffect(() => {
    if (!api || !pinnableEnabled) {
      return;
    }

    const allColumnIds = api
      .getAllDisplayedColumns()
      .map(col => col.getId() as keyof RowData & string);

    const pinState = allColumnIds.map(colId => ({
      colId,
      pinned: pinnedColumns[colId] || null,
    }));

    api.applyColumnState({ state: pinState });
  }, [api, pinnableEnabled, pinnedColumns]);

  const pinningGridProps: GridOptions<Row<RowData, RowId>> | undefined =
    pinnable
      ? {
          onColumnPinned: getOnColumnPinned(pinnableRef),
        }
      : undefined;

  return {
    pinnedColumns: pinnableEnabled ? pinnedColumns : null,
    pinningGridProps,
  };
};
